import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router'
import { lazy, Suspense, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { AuthProvider } from './lib/auth-context'
import { Spinner } from '@/components/ui/spinner'
import { colors } from '@/lib/tokens'
import Home from './pages/Home'
import Login from './pages/Login'
import DashboardLayout from './pages/dashboard/DashboardLayout'
import GettingStarted from './pages/dashboard/GettingStarted'
import Projects from './pages/dashboard/Projects'
import ProjectDetail from './pages/dashboard/ProjectDetail'
import Account from './pages/dashboard/Account'
import AutoParser from './pages/dashboard/AutoParser'
import DevRoleSwitcher from '@/components/dev/DevRoleSwitcher'

const Overview = lazy(() => import('@/pages/Overview'))
const Pricing = lazy(() => import('@/pages/Pricing'))
const Feedback = lazy(() => import('@/pages/Feedback'))
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('@/pages/TermsOfService'))
const Changelog = lazy(() => import('@/pages/Changelog'))
const DocsIndex = lazy(() => import('@/pages/docs/DocsIndex'))
const DocsApi = lazy(() => import('@/pages/docs/DocsApi'))
const DocsSDK = lazy(() => import('@/pages/docs/DocsSDK'))
const DocsExtension = lazy(() => import('@/pages/docs/DocsExtension'))

function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center" style={{ backgroundColor: colors.bgPage }}>
      <Spinner />
    </div>
  )
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30000, retry: 1 } } })

const SKIP_ONBOARDING = import.meta.env.VITE_DEV_SKIP_ONBOARDING === 'true';
const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true';

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster position="bottom-right" theme="dark" />
        {DEV_AUTH && <DevRoleSwitcher />}
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/overview" element={<Overview />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/docs" element={<DocsIndex />} />
              <Route path="/docs/api" element={<DocsApi />} />
              <Route path="/docs/extension" element={<DocsExtension />} />
              <Route path="/docs/sdk" element={<DocsSDK />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Navigate to={SKIP_ONBOARDING ? 'projects' : 'getting-started'} replace />} />
                <Route path="getting-started" element={<GettingStarted />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="parser" element={<AutoParser />} />
                <Route path="account" element={<Account />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
